/**
 * VLT — header behaviour.
 *
 * The navigation works without this file: panels open on :hover and
 * :focus-within in CSS, the drawer's accordions are native <details>, and the
 * search form is a plain GET. This script layers on the things CSS cannot do
 * — click-to-toggle, Escape-to-close, touch handling, aria bookkeeping, the
 * announcement rotator and scroll locking.
 */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktop = window.matchMedia('(min-width: 990px)');

  /* ----------------------------------------------------------------
   * Mega-menu panels
   * ---------------------------------------------------------------- */

  function initPanels(header) {
    if (header.dataset.vltPanelsBound) return;
    header.dataset.vltPanelsBound = 'true';

    var items = header.querySelectorAll('[data-vlt-nav-item]');
    var overlay = document.querySelector('[data-vlt-overlay]');
    var closeTimer = null;

    function setOverlay(visible) {
      if (!overlay) return;
      if (visible) {
        overlay.hidden = false;
        // Force a frame so the opacity transition actually runs.
        requestAnimationFrame(function () {
          overlay.setAttribute('data-visible', '');
        });
      } else {
        overlay.removeAttribute('data-visible');
        overlay.hidden = true;
      }
    }

    function close(item) {
      item.removeAttribute('data-open');
      var trigger = item.querySelector('[data-vlt-panel-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    function closeAll() {
      items.forEach(close);
      setOverlay(false);
    }

    function open(item) {
      items.forEach(function (other) {
        if (other !== item) close(other);
      });
      item.setAttribute('data-open', '');
      var trigger = item.querySelector('[data-vlt-panel-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      setOverlay(true);
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-vlt-panel-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (item.hasAttribute('data-open')) {
          closeAll();
        } else {
          open(item);
        }
      });

      // Pointer intent: open immediately, close on a short delay so moving
      // diagonally from the trigger into the panel does not dismiss it.
      item.addEventListener('mouseenter', function () {
        if (!desktop.matches) return;
        window.clearTimeout(closeTimer);
        open(item);
      });

      item.addEventListener('mouseleave', function () {
        if (!desktop.matches) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(closeAll, 160);
      });

      // Tabbing out of the whole item closes it.
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close(item);
      });
    });

    header.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openItem = header.querySelector('[data-vlt-nav-item][data-open]');
      if (!openItem) return;
      var trigger = openItem.querySelector('[data-vlt-panel-trigger]');
      closeAll();
      if (trigger) trigger.focus();
    });

    if (overlay) overlay.addEventListener('click', closeAll);

    document.addEventListener('click', function (event) {
      if (!header.contains(event.target)) closeAll();
    });
  }

  /* ----------------------------------------------------------------
   * Header search
   * ---------------------------------------------------------------- */

  function initSearch(header) {
    var toggle = header.querySelector('[data-vlt-search-toggle]');
    var panel = header.querySelector('#VltSearch');
    var closeButton = header.querySelector('[data-vlt-search-close]');
    var input = header.querySelector('#VltSearchInput');
    if (!toggle || !panel) return;
    if (toggle.dataset.vltBound) return;
    toggle.dataset.vltBound = 'true';

    function setOpen(open) {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && input) input.focus();
      if (!open) toggle.focus();
    }

    toggle.addEventListener('click', function () {
      setOpen(panel.hidden);
    });

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        setOpen(false);
      });
    }

    panel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setOpen(false);
    });
  }

  /* ----------------------------------------------------------------
   * Mobile drawer
   * ---------------------------------------------------------------- */

  function initDrawer() {
    var drawer = document.querySelector('[data-vlt-drawer]');
    var toggle = document.querySelector('[data-vlt-drawer-toggle]');
    var closeButton = document.querySelector('[data-vlt-drawer-close]');
    if (!drawer || !toggle) return;
    if (drawer.dataset.vltBound) return;
    drawer.dataset.vltBound = 'true';

    var lastFocused = null;

    function setOpen(open) {
      if (open) {
        lastFocused = document.activeElement;
        drawer.hidden = false;
        requestAnimationFrame(function () {
          drawer.setAttribute('data-open', '');
        });
        document.documentElement.style.overflow = 'hidden';
        if (closeButton) closeButton.focus();
      } else {
        drawer.removeAttribute('data-open');
        document.documentElement.style.overflow = '';
        // Wait out the slide-out before removing it from the tree.
        var delay = reducedMotion.matches ? 0 : 420;
        window.setTimeout(function () {
          drawer.hidden = true;
        }, delay);
        if (lastFocused) lastFocused.focus();
      }
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    toggle.addEventListener('click', function () {
      setOpen(drawer.hidden);
    });

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        setOpen(false);
      });
    }

    drawer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setOpen(false);
    });

    // Only one accordion open at a time keeps the drawer scannable.
    drawer.querySelectorAll('details').forEach(function (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        drawer.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== details) other.open = false;
        });
      });
    });

    // Returning to desktop width leaves no orphaned scroll lock behind.
    desktop.addEventListener('change', function (event) {
      if (event.matches && !drawer.hidden) setOpen(false);
    });
  }

  /* ----------------------------------------------------------------
   * Announcement rotator
   * ---------------------------------------------------------------- */

  function initAnnouncement() {
    var bar = document.querySelector('.vlt-announcement');
    if (!bar) return;

    var messages = Array.prototype.slice.call(
      bar.querySelectorAll('[data-announcement-message]')
    );
    if (messages.length < 2) return;
    if (bar.dataset.vltBound) return;
    bar.dataset.vltBound = 'true';

    var index = 0;
    var timer = null;
    var paused = false;

    function show(next) {
      index = (next + messages.length) % messages.length;
      messages.forEach(function (message, i) {
        message.hidden = i !== index;
      });
    }

    function stop() {
      window.clearInterval(timer);
      timer = null;
    }

    function start() {
      var interval = parseInt(bar.getAttribute('data-autorotate'), 10);
      // No auto-advance when the merchant turned it off, or when the visitor
      // asked for reduced motion. The arrows still work in both cases.
      if (!interval || reducedMotion.matches) return;
      stop();
      timer = window.setInterval(function () {
        if (!paused) show(index + 1);
      }, interval * 1000);
    }

    var prev = bar.querySelector('[data-announcement-prev]');
    var next = bar.querySelector('[data-announcement-next]');
    if (prev) {
      prev.addEventListener('click', function () {
        show(index - 1);
        start();
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        show(index + 1);
        start();
      });
    }

    ['mouseenter', 'focusin'].forEach(function (type) {
      bar.addEventListener(type, function () {
        paused = true;
      });
    });
    ['mouseleave', 'focusout'].forEach(function (type) {
      bar.addEventListener(type, function () {
        paused = false;
      });
    });

    document.addEventListener('visibilitychange', function () {
      paused = document.hidden;
    });

    start();
  }

  function init() {
    var header = document.querySelector('[data-vlt-header]');
    if (header) {
      initPanels(header);
      initSearch(header);
    }
    initDrawer();
    initAnnouncement();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place, so re-bind after it does.
  document.addEventListener('shopify:section:load', init);
})();
